import React, { useState } from 'react'
import styled from 'styled-components'
import MiniSwitch from '../formUtils/MiniSwitch'
import CustomInput from '../general/Form'
import { useApi } from '../../hooks/useApi'

interface TokenListFormProps {
  onSuccess?: () => void
}

interface TokenFormData {
  address: string
  symbol: string
  name: string
  decimals: number
  chainId: number
  spamCounter: number
  priority: number
  enabled: boolean
  isNativeToken: boolean
  isWrappedNativeToken: boolean
  [key: string]: string | number | boolean | string[]
}

const TokenListForm: React.FC<TokenListFormProps> = ({ onSuccess = () => {} }) => {
  const { callApi } = useApi()
  const [formData, setFormData] = useState<TokenFormData>({
    address: '',
    symbol: '',
    name: '',
    decimals: 0,
    chainId: 0,
    priority: 0,
    spamCounter: 0,
    enabled: true,
    isNativeToken: false,
    isWrappedNativeToken: false,
  })
  const [message, setMessage] = useState('')
  const [advanced, setAdvanced] = useState(false)

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (advanced) {
        await callApi('/token-registry/tokens', { ...formData }, 'POST')
      } else {
        await callApi(`/token-registry/tokens/${formData.chainId}/${formData.address}`)
      }
      onSuccess()
    } catch (error) {
      console.error('Error creating token:', error)
    }
  }

  const toggle = (field: keyof TokenFormData) =>
    setFormData(prev => ({ ...prev, [field]: !prev[field] }))

  return (
    <Form onSubmit={handleFormSubmit}>
      {message !== '' ? (
        <FeedbackBanner>
          <span>{message}</span>
          <CloseBtn onClick={() => setMessage('')}>✕</CloseBtn>
        </FeedbackBanner>
      ) : (
        <>
          <FormTitle>New token registry</FormTitle>
          <FieldGrid>
            <CustomInput type="text" label="Address" name="Address" modal value={formData.address} onChange={(e: any) => setFormData({ ...formData, address: e.target.value })} required />
            <CustomInput type="number" label="Chain ID" name="Chain ID" modal value={formData.chainId} onChange={(e: any) => setFormData({ ...formData, chainId: parseInt(e.target.value, 10) })} required />
          </FieldGrid>

          {advanced && (
            <FieldGrid>
              <CustomInput type="text" label="Name" name="Name" modal value={formData.name} onChange={(e: any) => setFormData({ ...formData, name: e.target.value })} required />
              <CustomInput type="text" label="Symbol" name="Symbol" modal value={formData.symbol} onChange={(e: any) => setFormData({ ...formData, symbol: e.target.value })} required />
              <CustomInput type="number" label="Decimals" name="Decimals" modal value={formData.decimals} onChange={(e: any) => setFormData({ ...formData, decimals: parseInt(e.target.value, 10) })} required />
              <CustomInput type="number" label="Spam Counter" name="Spam Counter" modal value={formData.spamCounter} onChange={(e: any) => setFormData({ ...formData, spamCounter: parseInt(e.target.value, 10) })} required />
              <CustomInput type="number" label="Priority" name="Priority" modal value={formData.priority} onChange={(e: any) => setFormData({ ...formData, priority: parseInt(e.target.value, 10) })} required />
              <SwitchField>
                <SwitchLabel>Native</SwitchLabel>
                <MiniSwitch ison={formData.isNativeToken} onToggle={() => toggle('isNativeToken')} />
              </SwitchField>
              <SwitchField>
                <SwitchLabel>Wrapped</SwitchLabel>
                <MiniSwitch ison={formData.isWrappedNativeToken} onToggle={() => toggle('isWrappedNativeToken')} />
              </SwitchField>
              <SwitchField>
                <SwitchLabel>Enabled</SwitchLabel>
                <MiniSwitch ison={formData.enabled} onToggle={() => toggle('enabled')} />
              </SwitchField>
            </FieldGrid>
          )}

          <Actions>
            <SubmitBtn type="submit">{advanced ? 'Add token' : 'Find token'}</SubmitBtn>
            <ToggleBtn type="button" onClick={() => setAdvanced(!advanced)}>
              {advanced ? 'Hide advanced' : 'Advanced'}
            </ToggleBtn>
          </Actions>
        </>
      )}
    </Form>
  )
}

export default TokenListForm

// ─── Styles ──────────────────────────────────────────────────────────────────

const Form = styled.form`display: flex; flex-direction: column; gap: 16px;`
const FormTitle = styled.h2`font-size: 14px; font-weight: 600; color: #ededed; margin: 0;`
const FieldGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 8px; align-items: start;`
const SwitchField = styled.div`display: flex; flex-direction: column; gap: 8px;`
const SwitchLabel = styled.label`font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: #777;`
const Actions = styled.div`display: flex; gap: 8px; align-items: center;`
const SubmitBtn = styled.button`height: 34px; padding: 0 16px; background: rgba(111,92,230,0.15); border: 1px solid rgba(111,92,230,0.35); border-radius: 6px; color: #a89aff; font-size: 13px; font-weight: 500; cursor: pointer; transition: background 0.15s, border-color 0.15s; &:hover { background: rgba(111,92,230,0.25); border-color: rgba(111,92,230,0.5); }`
const ToggleBtn = styled.button`height: 34px; padding: 0 14px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; color: #777; font-size: 13px; cursor: pointer; &:hover { border-color: rgba(255,255,255,0.15); color: #a0a0a0; }`
const FeedbackBanner = styled.div`display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: rgba(51,194,176,0.08); border: 1px solid rgba(51,194,176,0.25); border-radius: 6px; color: #33C2B0; font-size: 13px;`
const CloseBtn = styled.button`background: transparent; border: none; color: inherit; cursor: pointer; font-size: 12px; opacity: 0.7; &:hover { opacity: 1; }`
